import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Loader2, Sparkles, TrendingUp } from "lucide-react";

export default function AdminDigest() {
  const [preview, setPreview] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pTestTo, setPTestTo] = useState("");
  const [pBusy, setPBusy] = useState(false);
  const [pResult, setPResult] = useState(null);
  const [abStats, setAbStats] = useState(null);
  const [abCampaign, setAbCampaign] = useState("personalized_digest");

  const loadStats = (campaign = abCampaign) =>
    api.get("/api/admin/digest/ab-stats", { params: { campaign } })
      .then(({data}) => setAbStats(data)).catch(() => {});

  useEffect(() => {
    api.get("/api/admin/digest/preview").then(({data}) => setPreview(data));
    loadStats("personalized_digest");
  }, []);

  const switchCampaign = (c) => { setAbCampaign(c); loadStats(c); };

  const sendNow = async (testMode) => {
    setBusy(true); setResult(null);
    try {
      const params = testMode && testTo ? { test_to: testTo } : {};
      const { data } = await api.post("/api/admin/digest/send-now", null, { params });
      setResult(data);
      if (data.skipped_reason) toast.info("No new courses in the last 7 days — nothing to send.");
      else toast.success(`Digest sent to ${data.sent} recipient${data.sent !== 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const sendPersonalized = async (testMode) => {
    setPBusy(true); setPResult(null);
    try {
      const params = testMode && pTestTo ? { test_to: pTestTo } : {};
      const { data } = await api.post("/api/admin/digest/personalized/send-now", null, { params });
      setPResult(data);
      toast.success(`Personalized digest sent to ${data.sent} recipient${data.sent !== 1 ? "s" : ""}`);
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setPBusy(false); }
  };

  return (
    <div data-testid="admin-digest">
      <div className="label-eyebrow">Email</div>
      <h1 className="font-display font-black text-3xl mt-1">Email Digests</h1>
      <p className="text-sm text-[#4B5563] mt-1 max-w-2xl">
        Two automated emails: <strong>Weekly New Courses</strong> (Mon 14:00 UTC) and
        <strong> Personalized "Picked for you"</strong> (Fri 14:00 UTC). Test or trigger them below.
      </p>

      <div className="mt-8 bg-white border border-[#E5E7EB] rounded-xl p-6">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#D92D20]" />
          <h2 className="font-display font-bold text-xl">Weekly New Courses</h2>
        </div>
        <p className="text-xs text-[#4B5563] mt-1">Runs every Monday 14:00 UTC. Shows up to 6 published courses created in the last 7 days.</p>

        <div className="mt-4">
          <div className="label-eyebrow">Next digest preview</div>
          {preview.length === 0 ? (
            <div className="text-sm text-[#4B5563] mt-2">No new courses in the last 7 days — Monday's send will be skipped.</div>
          ) : (
            <ul className="mt-2 space-y-2">
              {preview.map((c) => (
                <li key={c.id} className="flex items-center gap-3 text-sm">
                  <img src={c.thumbnail_url} alt="" className="w-14 h-10 object-cover rounded border border-[#E5E7EB]" />
                  <span className="font-semibold">{c.title}</span>
                  <span className="text-xs text-[#4B5563]">${c.price_usd}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-[#E5E7EB] pt-5">
          <div className="flex-1 min-w-[240px]">
            <label className="text-sm font-semibold">Test recipient</label>
            <input data-testid="digest-test-email" type="email" value={testTo}
              onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com"
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <button onClick={() => sendNow(true)} disabled={busy || !testTo} data-testid="digest-send-test"
            className="btn-outline flex items-center gap-1">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send test
          </button>
          <button onClick={() => sendNow(false)} disabled={busy} data-testid="digest-send-all"
            className="btn-primary flex items-center gap-1">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to all
          </button>
        </div>
        {result && (
          <pre className="mt-3 text-xs bg-[#FAFAFA] border border-[#E5E7EB] rounded-md p-3 font-mono whitespace-pre-wrap" data-testid="digest-result">
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </div>

      <div className="mt-8 bg-white border border-[#E5E7EB] rounded-xl p-6">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#064E3B]" />
          <h2 className="font-display font-bold text-xl">Personalized "Picked for you"</h2>
        </div>
        <p className="text-xs text-[#4B5563] mt-1">
          Runs every Friday 14:00 UTC. Each student gets 3 courses from categories they're already learning in
          (or popular picks if they haven't enrolled yet).
        </p>

        <div className="mt-5 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <label className="text-sm font-semibold">Test recipient</label>
            <input data-testid="personalized-test-email" type="email" value={pTestTo}
              onChange={(e) => setPTestTo(e.target.value)} placeholder="you@example.com"
              className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm" />
          </div>
          <button onClick={() => sendPersonalized(true)} disabled={pBusy || !pTestTo} data-testid="personalized-send-test"
            className="btn-outline flex items-center gap-1">
            {pBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send test
          </button>
          <button onClick={() => sendPersonalized(false)} disabled={pBusy} data-testid="personalized-send-all"
            className="btn-secondary flex items-center gap-1">
            {pBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to all
          </button>
        </div>
        {pResult && (
          <pre className="mt-3 text-xs bg-[#FAFAFA] border border-[#E5E7EB] rounded-md p-3 font-mono whitespace-pre-wrap" data-testid="personalized-result">
            {JSON.stringify(pResult, null, 2)}
          </pre>
        )}
      </div>

      {/* A/B TEST RESULTS */}
      {abStats && (
        <div className="mt-8 bg-white border border-[#E5E7EB] rounded-xl p-6" data-testid="ab-stats">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#D97706]" />
              <h2 className="font-display font-bold text-xl">A/B Test — Subject Lines</h2>
            </div>
            <div className="inline-flex border border-[#E5E7EB] rounded-md overflow-hidden" data-testid="ab-campaign-tabs">
              {[
                {id:"personalized_digest", label:"Picked for you"},
                {id:"welcome", label:"Welcome"},
                {id:"weekly_new", label:"Weekly new"},
              ].map((t) => (
                <button key={t.id} onClick={() => switchCampaign(t.id)}
                  data-testid={`ab-tab-${t.id}`}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    abCampaign === t.id ? "bg-[#D92D20] text-white" : "bg-white text-[#111827] hover:bg-[#FAFAFA]"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-[#4B5563] mt-1">{abStats.tracking_note}</p>

          {/* Significance / Promotion banner */}
          <div className="mt-4 rounded-md p-4 border" style={{
            background: abStats.promoted_variant ? "#064E3B" : "#FAFAFA",
            color: abStats.promoted_variant ? "white" : "#111827",
            borderColor: abStats.promoted_variant ? "#064E3B" : "#E5E7EB",
          }} data-testid="ab-promotion-banner">
            {abStats.promoted_variant ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="label-eyebrow" style={{color:"#FECACA"}}>🏆 Winner promoted</div>
                  <div className="font-display font-bold mt-1">
                    Variant {abStats.promoted_variant} — all sends now use this subject.
                  </div>
                </div>
                <button onClick={async () => { await api.post(`/api/admin/digest/ab-reset?campaign=${abCampaign}`); loadStats(abCampaign); toast.success("Promotion reset"); }}
                  data-testid="ab-reset" className="btn-outline text-sm" style={{background:"white"}}>
                  Reset to A/B testing
                </button>
              </div>
            ) : (
              <div className="text-sm">
                <div className="label-eyebrow">Significance check</div>
                <div className="mt-1">
                  {abStats.significance.reason === "insufficient_data" && (
                    <>Need at least <strong>{abStats.significance.min_sample_required} delivered sends per variant</strong> before significance is tested. Keep gathering data.</>
                  )}
                  {abStats.significance.reason === "not_significant_yet" && (
                    <>Leader is <strong>variant {abStats.significance.leader}</strong> at {abStats.significance.leader_open_rate}% open rate, but the gap isn't statistically significant yet (≥95% confidence required). Keep sending.</>
                  )}
                  {abStats.significance.reason === "significant" && (
                    <>🏆 Variant <strong>{abStats.significance.winner}</strong> wins at ≥95% confidence. Promote it below or wait for the next batch to auto-promote.</>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 overflow-hidden border border-[#E5E7EB] rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F4F6]">
                <tr>
                  <th className="px-3 py-2 text-left">Variant</th>
                  <th className="px-3 py-2 text-left">Subject template</th>
                  <th className="px-3 py-2 text-right">Sent</th>
                  <th className="px-3 py-2 text-right">Opened</th>
                  <th className="px-3 py-2 text-right">Clicked</th>
                  <th className="px-3 py-2 text-right">Open %</th>
                  <th className="px-3 py-2 text-right">Click %</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {abStats.variants.map((v) => {
                  const isWinner = abStats.promoted_variant === v.variant_id;
                  return (
                    <tr key={v.variant_id} className={`border-t border-[#E5E7EB] ${isWinner ? "bg-[#064E3B]/5" : ""}`} data-testid={`ab-row-${v.variant_id}`}>
                      <td className="px-3 py-2 font-bold">
                        {v.variant_id}{isWinner && <span className="ml-2 text-xs bg-[#064E3B] text-white px-1.5 py-0.5 rounded uppercase">winner</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{v.subject_template}</td>
                      <td className="px-3 py-2 text-right">{v.sent}</td>
                      <td className="px-3 py-2 text-right">{v.opened}</td>
                      <td className="px-3 py-2 text-right">{v.clicked}</td>
                      <td className="px-3 py-2 text-right">{v.open_rate}%</td>
                      <td className="px-3 py-2 text-right">{v.click_rate}%</td>
                      <td className="px-3 py-2 text-right">
                        {!isWinner && (
                          <button data-testid={`ab-promote-${v.variant_id}`}
                            onClick={async () => {
                              await api.post(`/api/admin/digest/ab-promote?campaign=${abCampaign}&variant_id=${v.variant_id}`);
                              loadStats(abCampaign);
                              toast.success(`Promoted ${v.variant_id}`);
                            }}
                            className="text-xs font-semibold text-[#D92D20] hover:underline">
                            Promote
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <details className="mt-4 text-xs text-[#4B5563]">
            <summary className="cursor-pointer font-semibold">How to enable opens & clicks tracking →</summary>
            <ol className="mt-2 ml-5 list-decimal space-y-1">
              <li>Go to https://resend.com/webhooks and click <strong>Add Endpoint</strong>.</li>
              <li>Endpoint URL: <code>{window.location.origin}/api/webhooks/resend</code></li>
              <li>Subscribe to events: <code>email.opened</code>, <code>email.clicked</code>, <code>email.delivered</code>.</li>
              <li>Also turn on Open / Click tracking in your Resend domain settings.</li>
              <li>Open & Click counts will start populating automatically.</li>
            </ol>
          </details>
        </div>
      )}
    </div>
  );
}
